import React from 'react';
import { Modal } from 'flowbite-react';
import { Icon } from '../Icon';

type ChangeModalFrameProps = {
    standalone?: boolean;
    show: boolean;
    onClose: () => void;
    dismissible: boolean;
    isExpanded: boolean;
    /** Overlay expanded only — viewport-bounded panes. Standalone grows naturally. */
    lockPaneHeights?: boolean;
    sideOffset?: boolean;
    sideOffsetClass?: string;
    children: React.ReactNode;
};

/** Modal or full-page frame around change detail content. */
export function ChangeModalFrame(props: ChangeModalFrameProps) {
    const {
        standalone, show, onClose, dismissible, isExpanded, lockPaneHeights,
        sideOffset, sideOffsetClass, children,
    } = props;

    if (standalone) {
        // Natural height: App outlet is the only scroller. Do not lock to viewport
        // or nested panes swallow the wheel while the page cannot move.
        return (
            <div className="relative flex w-full flex-col rounded-lg border border-gray-200 bg-white shadow dark:border-gray-600 dark:bg-gray-700">
                {children}
            </div>
        );
    }

    return (
        <Modal
            onClose={onClose}
            dismissible={dismissible}
            size="3xl"
            show={show}
            className={`focus:outline-0${sideOffset ? ` ${sideOffsetClass || 'md:pr-[min(32rem,42vw)]'}` : ''}`}
            theme={{
                root: {
                    sizes: {
                        '3xl': isExpanded
                            ? 'w-[calc(100vw-2rem)] max-w-none'
                            : 'max-w-3xl',
                    },
                },
                content: {
                    base: isExpanded
                        ? 'relative h-full w-full p-2 md:h-full'
                        : 'relative h-full w-full p-4 md:h-auto',
                    inner: isExpanded
                        ? 'relative flex h-[calc(100vh-2rem)] max-h-[calc(100vh-2rem)] w-full flex-col overflow-hidden rounded-lg bg-white shadow dark:bg-gray-700'
                        : 'relative flex max-h-[90vh] flex-col overflow-hidden rounded-lg bg-white shadow dark:bg-gray-700',
                },
                body: {
                    base: lockPaneHeights
                        // Mobile: one scroll for stacked form+activity. Desktop: clip;
                        // panes scroll inside the progress grid.
                        ? 'flex-1 min-h-0 overflow-y-auto p-6 lg:overflow-hidden'
                        : 'flex-1 min-h-0 overflow-auto p-6',
                },
            }}
        >
            {children}
        </Modal>
    );
}

type ChangeModalHeaderProps = {
    standalone?: boolean;
    onClose: () => void;
    closeLabel?: string;
    dense?: boolean;
    children: React.ReactNode;
};

export function ChangeModalHeader(props: ChangeModalHeaderProps) {
    const { standalone, onClose, closeLabel, dense, children } = props;

    if (standalone) {
        return (
            <div className={`flex w-full justify-between rounded-t border-b dark:border-gray-600 ${dense ? 'items-center px-4 py-2' : 'items-start p-5'}`}>
                <div className={`w-full font-medium text-gray-900 dark:text-white ${dense ? 'min-w-0 text-sm' : 'text-xl'}`}>
                    {children}
                </div>
                <button
                    type="button"
                    className="ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 dark:hover:bg-gray-700 hover:text-gray-900 dark:text-gray-100 dark:hover:bg-gray-600 dark:hover:text-white"
                    onClick={onClose}
                    aria-label={closeLabel || 'Close'}
                >
                    <Icon type="close" size={5} color="current" />
                </button>
            </div>
        );
    }

    return (
        <Modal.Header
            className="w-full"
            theme={{
                base: dense
                    ? 'flex items-center justify-between rounded-t border-b px-4 py-2 dark:border-gray-600'
                    : 'flex items-start justify-between rounded-t border-b p-5 dark:border-gray-600',
                title: dense
                    ? 'min-w-0 text-sm font-medium text-gray-900 dark:text-white w-full'
                    : 'text-xl font-medium text-gray-900 dark:text-white w-full',
                close: {
                    base: 'ml-auto inline-flex items-center rounded-lg bg-transparent p-1.5 text-sm text-gray-400 hover:bg-gray-200 hover:text-gray-900 dark:text-gray-300 dark:hover:bg-gray-600 dark:hover:text-white',
                    icon: 'h-5 w-5',
                },
            }}
        >
            {children}
        </Modal.Header>
    );
}

type ChangeModalBodyProps = {
    standalone?: boolean;
    isExpanded: boolean;
    lockPaneHeights?: boolean;
    children: React.ReactNode;
};

export function ChangeModalBody(props: ChangeModalBodyProps) {
    const { standalone, isExpanded, children } = props;

    if (standalone) {
        return (
            <div data-change-modal-body className="flex flex-col p-6">
                {children}
            </div>
        );
    }

    return (
        <Modal.Body
            data-change-modal-body
            style={{ minHeight: isExpanded ? undefined : '500px' }}
            className="min-h-0"
        >
            {children}
        </Modal.Body>
    );
}
